import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, CalendarClock, MessageSquare, LogOut, Home, ChevronDown } from "lucide-react";
import { SITE } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import Dashboard from "@/components/portal/Dashboard";
import Documents from "@/components/portal/Documents";
import Appointments from "@/components/portal/Appointments";
import Messages from "@/components/portal/Messages";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "appointments", label: "Appointments", icon: CalendarClock },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || "C").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#f4f7fd] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-[#061a3a] text-slate-300 sticky top-0 h-screen" data-testid="portal-sidebar">
        <div className="p-6 border-b border-white/10">
          <img src={SITE.logo} alt="Sagos" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1.5" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`nav-${n.key}`}
              onClick={() => setView(n.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                view === n.key ? "bg-primary text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200">
            <Home size={18} /> Back to website
          </button>
          <button data-testid="sidebar-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border">
          <div className="px-5 lg:px-10 h-16 flex items-center justify-between">
            {/* mobile nav */}
            <div className="flex items-center gap-1 lg:hidden overflow-x-auto no-scrollbar">
              {NAV.map((n) => (
                <button
                  key={n.key}
                  onClick={() => setView(n.key)}
                  data-testid={`mobile-nav-${n.key}`}
                  className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${view === n.key ? "bg-primary text-white" : "text-slate-500"}`}
                >
                  {n.label}
                </button>
              ))}
            </div>
            <div className="hidden lg:block" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors">
                  <span className="h-9 w-9 rounded-full bg-[#0b2a5b] text-white flex items-center justify-center text-sm font-medium">{initials}</span>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown size={16} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/")} data-testid="menu-home"><Home size={15} className="mr-2" /> Back to website</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive focus:text-destructive"><LogOut size={15} className="mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-10 max-w-6xl w-full">
          {view === "dashboard" && <Dashboard onNavigate={setView} />}
          {view === "documents" && <Documents />}
          {view === "appointments" && <Appointments />}
          {view === "messages" && <Messages />}
        </main>
      </div>
    </div>
  );
}
